/* Grey Chapel — gc-core.js
   Shared helpers, the GC namespace, and the progressive-image reveal.
   Must load first: everything else reads window.GC. See docs/gc-core.md. */
(function () {
  'use strict';
  var GC = window.GC = window.GC || {};

  var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* One boundary per subsystem: a throw reaches the console, not the next caller. */
  function safe(fn) {
    try { fn(); } catch (e) {
      if (window.console && console.error) console.error(e);
      /* Re-thrown in a task of its own. Containment is unchanged — the caller carries
         on synchronously — but the error also reaches window.onerror, and so reaches
         Clarity. console.error alone is invisible to it, which is how a real fault in
         here could look exactly like a clean session. */
      setTimeout(function () { throw e; }, 0);
    }
  }

  /* Same idea per element: one broken product section must not stop the next. */
  function safeAll(sel, fn) {
    safe(function () {
      Array.prototype.forEach.call(document.querySelectorAll(sel), function (el) {
        safe(function () { fn(el); });
      });
    });
  }

  /* Two decimals always. The shop's money_format is ${{amount}}, which Liquid renders
     as $70.00 — dropping to $70 here put a different figure on screen than the one the
     server sends back a moment later. Unreachable while every shirt is $34.99, and not
     worth leaving armed. */
  function money(cents) {
    return '$' + (cents / 100).toFixed(2);
  }


  /* ---------------- Progressive images ---------------- */

  /* #t3r8v Marks .is-loaded once a rendition decodes, by four independent paths, taking the placeholder and sheen off. See docs/gc-core.md. */

  function progressiveDone(img) {
    if (!img || !img.classList || img.classList.contains('is-loaded')) return;
    img.classList.add('is-loaded');
  }

  /* Takes a scope so markup arriving after DOMContentLoaded is bound too — the
     related-products grid is fetched and injected further down the page. */
  function initProgressiveImages(scope) {
    try {
      var root = scope && scope.querySelectorAll ? scope : document;
      var imgs = root.querySelectorAll('img.gc-progressive:not(.is-loaded)');
      Array.prototype.forEach.call(imgs, function (img) {
        if (img.gcProgBound) return;
        img.gcProgBound = true;

        /* Bound before the cached check and never removed: callers that swap src later rely on the next load event. */
        img.addEventListener('load', function () { progressiveDone(img); });
        img.addEventListener('error', function () { progressiveRetry(img); });

        if (progressiveDecoded(img)) progressiveDone(img);
        else if (progressiveFailed(img)) progressiveRetry(img);
      });
    } catch (e) {}
  }

  // #m4x9r One more request for a failed rendition, sheen kept, before an empty frame is accepted; waits for 'online' when offline.
  function progressiveRetry(img) {
    if (img.gcRetryPending || img.classList.contains('is-loaded')) return;
    var url = img.currentSrc || img.getAttribute('src') || '';
    if (img.gcRetriedUrl === url) { progressiveDone(img); return; }
    img.gcRetriedUrl = url;
    img.gcRetryPending = true;
    var again = function () {
      img.gcRetryPending = false;
      if (!img.isConnected) return;
      // Re-setting the attributes to the same value is what makes the browser request them again.
      var set = img.getAttribute('srcset'), src = img.getAttribute('src');
      if (set) img.setAttribute('srcset', set);
      if (src) img.setAttribute('src', src);
    };
    if (navigator.onLine === false) window.addEventListener('online', again, { once: true });
    else setTimeout(again, 1500);
  }

  // #h7c2w An eager image that failed before this script bound its error listener; lazy ones are excluded, unstarted looks the same.
  function progressiveFailed(img) {
    return !!(img && img.complete && img.naturalWidth === 0 && img.loading !== 'lazy' && (img.currentSrc || img.getAttribute('src')));
  }

  /* naturalWidth, not `complete`: a lazy image the browser has not started reports
     complete === true with nothing behind it, so testing that alone drops the
     placeholder at parse time and leaves an empty frame. */
  function progressiveDecoded(img) {
    return !!(img && img.complete && img.naturalWidth > 0);
  }

  /* Clears anything still marked loading whose picture has in fact decoded. Cheap
     enough to run on any event that might have hidden a load from us. */
  function sweepProgressive() {
    try {
      var imgs = document.querySelectorAll('img.gc-progressive:not(.is-loaded)');
      Array.prototype.forEach.call(imgs, function (img) {
        if (progressiveDecoded(img)) progressiveDone(img);
        else if (progressiveFailed(img)) progressiveRetry(img);
      });
    } catch (e) {}
  }

  function bootProgressive(scope) {
    initProgressiveImages(scope);
    sweepProgressive();
    /* Tells the <head> watchdog the sheen has an owner. Set last, so a throw
       above leaves the flag off and the watchdog lifts .gc-loadfx at load. */
    document.documentElement.classList.add('gc-prog-ok');
  }

  /* Published before the boot below runs, so a throw in bootProgressive()
     cannot leave the other modules without their helpers. */
  GC.root = root;
  GC.safe = safe;
  GC.safeAll = safeAll;
  GC.money = money;
  /* #r7m4d The shopper's last tapped size, kept across sessions in localStorage. See docs/remembered-size.md. */
  var SIZE_KEY = 'gc-size';
  function sizeKey(s) {
    return String(s || '').toLowerCase().replace(/[\s.-]/g, '')
      .replace('extra', 'x').replace('large', 'l').replace('medium', 'm').replace('small', 's');
  }
  GC.size = {
    remember: function (value) {
      try { localStorage.setItem(SIZE_KEY, value); }
      catch (err) { console.warn('gc-size: localStorage unavailable, size not remembered', err); }
    },
    recall: function () {
      try { return localStorage.getItem(SIZE_KEY) || ''; }
      catch (err) { console.warn('gc-size: localStorage unavailable', err); return ''; }
    },
    /* The value from `values` that names the same size, so "XL" finds "Extra Large". */
    match: function (stored, values) {
      var k = sizeKey(stored);
      if (!k) return '';
      for (var i = 0; i < values.length; i++) if (sizeKey(values[i]) === k) return values[i];
      return '';
    }
  };

  GC.progressive = {
    done: progressiveDone,
    init: initProgressiveImages,
    decoded: progressiveDecoded,
    boot: bootProgressive
  };

  /* #fbx3e Replaying bus: a subscriber that loads after an emit still receives it,
     and every past emit, so module script order is not load-bearing. */
  var subs = {}, sent = {};
  GC.on = function (name, fn) {
    (subs[name] = subs[name] || []).push(fn);
    (sent[name] || []).forEach(function (p) { safe(function () { fn(p); }); });
  };
  GC.emit = function (name, payload) {
    (sent[name] = sent[name] || []).push(payload);
    (subs[name] || []).forEach(function (fn) { safe(function () { fn(payload); }); });
  };

  /* Per-element inits, run now and again whenever Shopify's editor replaces a
     section's markup — that event only re-ran the image boot before, so editing
     the product section left its picker and gallery dead until a reload. */
  var inits = [];
  GC.register = function (selector, fn) {
    inits.push({ sel: selector, fn: fn });
    safeAll(selector, fn);
  };
  GC.boot = function (scope) {
    if (!scope || !scope.querySelectorAll) scope = document;
    inits.forEach(function (it) {
      safe(function () {
        if (scope.matches && scope.matches(it.sel)) safe(function () { it.fn(scope); });
        Array.prototype.forEach.call(scope.querySelectorAll(it.sel), function (el) {
          safe(function () { it.fn(el); });
        });
      });
    });
  };

  /* #vf6ji An open modal owns one history entry, so the phone's back button closes it
     instead of leaving the page. The drawer keeps its own copy in gc-cart.js.
     See docs/gc-core.md, "Back closes a modal". */
  var layerOpen = null, layerPending = null, layerUnwinding = false;
  function layerPush(l) {
    try { history.pushState({ gcLayer: true }, ''); layerOpen = l; }
    catch (err) { console.warn('gc-layer: no history entry, back will leave the page', err); }
  }
  GC.backLayer = function (onBack) {
    var l = { onBack: onBack };
    l.open = function () {
      if (layerOpen === l || layerPending === l) return;
      if (layerUnwinding) { layerPending = l; return; }
      if (layerOpen) return;
      layerPush(l);
    };
    l.close = function () {
      if (layerPending === l) { layerPending = null; return; }
      if (layerOpen !== l) return;
      layerOpen = null;
      layerUnwinding = true;
      history.back();
    };
    return l;
  };
  window.addEventListener('popstate', function () {
    if (layerUnwinding) {
      layerUnwinding = false;
      if (layerPending) { var p = layerPending; layerPending = null; layerPush(p); }
      return;
    }
    if (layerOpen) { var l = layerOpen; layerOpen = null; safe(l.onBack); }
  });

  /* #3wjc8 ---------------- Prefetch the product-page modules ----------------

     Set only off a product page. Fetched after load at the browser's lowest
     priority so nothing on screen competes with it. Data Saver is the only
     refusal — a deliberately weaker test than gc-zoom's image warm, which wants a
     fast link before spending megabytes; this is ~27 KB. See docs/gc-core.md. */

  window.addEventListener('load', function () {
    safe(function () {
      var urls = window.GC_PREFETCH;
      if (!urls || !urls.length) return;
      var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && conn.saveData) return;

      var send = function () {
        urls.forEach(function (href) {
          var link = document.createElement('link');
          link.rel = 'prefetch';
          link.as = href.indexOf('.css') === -1 ? 'script' : 'style';
          link.href = href;
          document.head.appendChild(link);
        });
      };
      if (window.requestIdleCallback) requestIdleCallback(send, { timeout: 3000 });
      else setTimeout(send, 500);
    });
  });

  /* #nqk8a ---------------- Warm the tiles below the fold ----------------

     loading="lazy" only fetches a tile as it nears the viewport, so half a minute
     spent reading the top of a collection buys the rows underneath nothing — the
     scroll then waits on every one of them.

     These are bytes the scroll was going to spend anyway; warming only moves them
     earlier. That is why the refusal is the prefetch's and not gc-zoom's: Data Saver
     stops it, and a 2g link gets a short lookahead instead of the whole grid. Only
     someone who lands and leaves without scrolling pays anything extra.

     Promoted by setting `loading` on the element. Never `new Image().src` — these are
     <picture>, and a bare Image warms the desktop candidate on a phone. */

  var WARM_BATCH = 4;      /* in flight at once — enough to keep the link busy */
  var WARM_STALL = 5000;   /* ms before a silent image stops holding up the queue */

  function warmTiles() {
    var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (conn && conn.saveData) return;

    var pending = Array.prototype.slice.call(
      document.querySelectorAll('.gc-card-media img[loading="lazy"]')
    ).filter(function (img) { return !img.gcWarmed; });
    if (!pending.length) return;

    /* Document order is scroll order, so the queue is already in the right sequence. */
    if (conn && (conn.effectiveType === '2g' || conn.effectiveType === 'slow-2g')) {
      pending = pending.slice(0, 8);
    }
    pending.forEach(function (img) { img.gcWarmed = true; });

    var next = function () {
      if (!pending.length) return;
      var batch = pending.splice(0, WARM_BATCH);
      var left = batch.length;
      var done = function () {
        if (--left > 0) return;
        /* Idle between batches, so a scroll or a cart request always takes the link
           ahead of a tile nobody has asked for yet. */
        if (window.requestIdleCallback) requestIdleCallback(next, { timeout: 1000 });
        else setTimeout(next, 100);
      };
      batch.forEach(function (img) {
        if (progressiveDecoded(img)) { done(); return; }
        var fired = false, timer;
        var once = function () {
          if (fired) return;
          fired = true;
          clearTimeout(timer);
          img.removeEventListener('load', once);
          img.removeEventListener('error', once);
          done();
        };
        timer = setTimeout(once, WARM_STALL);
        img.addEventListener('load', once);
        img.addEventListener('error', once);
        img.loading = 'eager';
      });
    };
    next();
  }

  /* Exposed because the related grid is injected after this has already run. */
  GC.warmTiles = function () { safe(warmTiles); };

  window.addEventListener('load', function () {
    safe(function () {
      if (window.requestIdleCallback) requestIdleCallback(warmTiles, { timeout: 2000 });
      else setTimeout(warmTiles, 600);
    });
  });

  /* ---------------- Progressive image boot ---------------- */

  /* Runs ahead of every other module and behind nothing that can throw — the exports
     above are plain assignments. bootProgressive is wrapped rather than passed to the
     listener, which would hand it the Event as its scope argument. */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bootProgressive(); });
  } else bootProgressive();

  window.addEventListener('load', sweepProgressive);
  /* bfcache restore: the page comes back with no load event of its own */
  window.addEventListener('pageshow', sweepProgressive);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) bootProgressive();
  });
  /* Theme editor: section markup is replaced wholesale on every save */
  document.addEventListener('shopify:section:load', function (e) {
    var scope = e && e.target ? e.target : document;
    bootProgressive(scope);
    GC.boot(scope);
  });

  /* #p2n7c ---------------- Hand the tapped tile to the product page ----------------
     Records the rendition the tile actually decoded, so the PDP can paint it from cache
     as its placeholder. The other half is inline on the hero slide. See docs/card-handoff.md. */

  document.addEventListener('click', function (e) {
    safe(function () {
      if (!document.documentElement.hasAttribute('data-handoff')) return;
      /* Tiles, and the cart's picks, whose link names the colour their image shows. */
      var a = e.target && e.target.closest && e.target.closest('.gc-card a[href*="/products/"], .gc-pick a[href*="/products/"], a.gc-predict__item[href*="/products/"]');
      if (!a) return;
      var src = '';
      var card = a.closest('.gc-card'), pick = !card && a.closest('.gc-pick');
      if (card || pick) {
        var img = card ? card.querySelector('.gc-card-media img') : pick.querySelector('.gc-pick__media img');
        if (progressiveDecoded(img)) src = img.currentSrc || img.src;   /* half-loaded: the PDP would start a second download */
      } else {
        /* #t4qe9 A search suggestion: this visit's decoded tile if it wore the default colour, else the suggestion's own thumbnail. */
        var m = /\/products\/([^/?#]+)/.exec(a.getAttribute('href'));
        var seen = m && GC.seen ? GC.seen(m[1]) : null;
        var thumb = a.querySelector('img');
        if (seen && !seen[1]) src = seen[0];
        else if (thumb && thumb.complete && thumb.naturalWidth > 0) src = thumb.currentSrc || thumb.src;
      }
      if (!src) return;
      try {
        sessionStorage.setItem('gc-handoff', JSON.stringify({ k: src.replace(/[?&]width=\d+/, ''), s: src }));
      } catch (err) {
        console.warn('gc-handoff: sessionStorage unavailable', err);
      }
    });
  }, true);

  /* #g6y2e Which rendition of each design this visit already decoded, and in which colour, kept
     as the shopper leaves each page, so the cart's picks draw from the browser cache. See docs/cart-picks.md. */
  var SEEN_KEY = 'gc-seen', SEEN_MAX = 300;
  function tilesDecoded(scope) {
    var out = {};
    Array.prototype.forEach.call((scope || document).querySelectorAll('.gc-card'), function (card) {
      var a = card.querySelector('a[href*="/products/"]'), img = card.querySelector('.gc-card-media img');
      if (!a || !progressiveDecoded(img)) return;
      var m = /\/products\/([^/?#]+)(?:\?(?:[^#]*&)?variant=(\d+))?/.exec(a.getAttribute('href') || '');
      if (m) out[m[1]] = [img.currentSrc || img.src, m[2] || ''];
    });
    return out;
  }
  function readSeen() {
    try { return JSON.parse(sessionStorage.getItem(SEEN_KEY) || '{}') || {}; }
    catch (err) { console.warn('gc-seen: sessionStorage unavailable', err); return {}; }
  }
  window.addEventListener('pagehide', function () {
    safe(function () {
      var map = readSeen(), now = tilesDecoded(), keys, k;
      for (k in now) { delete map[k]; map[k] = now[k]; }
      keys = Object.keys(map);
      for (k = 0; k < keys.length - SEEN_MAX; k++) delete map[keys[k]];
      try { sessionStorage.setItem(SEEN_KEY, JSON.stringify(map)); }
      catch (err) { console.warn('gc-seen: sessionStorage unavailable', err); }
    });
  });
  /* [src, variantId] for a design already decoded on this page or earlier this visit, else null. */
  GC.seen = function (handle) {
    return tilesDecoded()[handle] || readSeen()[handle] || null;
  };
})();
